import type { Stream, VideoPlayer } from '@zoom/videosdk';

import { RuntimeEnvironmentOptions, getEnvFromHostname } from '@growtherapy/analytics';

import { logger } from '../../../../../datadog/logger';
import { videoSDKMutex } from '../../../../core/mutex';
import { LocalVideoDelegator } from '../../../../core/room/participant/media/MediaDelegator';
import { VideoClient } from '../../../types';
import { handleError } from '../../../utils/handleError';

export class ZoomLocalVideoDelegator implements LocalVideoDelegator {
  readonly kind = 'video' as const;
  #stream: typeof Stream;
  #userId: number;
  #videoClient: VideoClient;

  constructor(userId: number, stream: typeof Stream, videoClient: VideoClient) {
    this.#userId = userId;
    this.#stream = stream;
    this.#videoClient = videoClient;
  }

  get deviceId() {
    return this.#stream.getActiveCamera();
  }

  get isEnabled() {
    return !!this.#stream;
  }

  private get isInSession() {
    return this.#videoClient.getSessionInfo()?.isInMeeting;
  }

  async attach(element: VideoPlayer, videoQuality?: number): Promise<void> {
    return videoSDKMutex.runExclusive(() => this.#attach(element, videoQuality), 'ZoomLocalVideoDelegator#attach');
  }

  async #attach(element: VideoPlayer, videoQuality?: number): Promise<void> {
    this.#logDebugInfo('[ZoomLocalVideo]: Attaching video track to element', {
      element,
      videoQuality,
    });

    try {
      await this.#stream.attachVideo(this.#userId, videoQuality || 1, element);
    } catch (attachVideoError) {
      handleError('[ZoomLocalVideo]: attach', attachVideoError);
    }
  }

  async detach(element: VideoPlayer): Promise<void> {
    return videoSDKMutex.runExclusive(() => this.#detach(element), 'ZoomLocalVideoDelegator#detach');
  }

  async #detach(element: VideoPlayer): Promise<void> {
    if (!this.isInSession) {
      logger.info('[ZoomLocalVideo]: Not in session, skipping detach');
      return;
    }

    this.#logDebugInfo('[ZoomLocalVideo]: Detaching video track from element', {
      element,
    });

    try {
      if (element) {
        // detach for the specific local video, whether thumbnail or settings
        await this.#stream.detachVideo(this.#userId, element);
      }
    } catch (detachVideoError) {
      handleError('[ZoomLocalVideo]: detach', detachVideoError);
    }
  }

  #logDebugInfo(...args: Parameters<typeof logger.debug>) {
    if (getEnvFromHostname({ hostname: location.hostname }) === RuntimeEnvironmentOptions.local) {
      const [message, context] = args;
      logger.debug(message, {
        this: {
          kind: this.kind,
          stream: this.#stream,
          userId: this.#userId,
          isEnabled: this.isEnabled,
        },
        ...context,
      });
    }
  }
}
